/**
 * @module cercaliagl/Geolocation
 */

import EventEmitter from 'eventemitter3';
import { IMG } from './globals';
import LonLat from './LonLat';
import { createCircleGeometry } from './util';

/**
 * @enum {string}
 */
export const EventType = {
  TRACKING: 'tracking',
  CHANGE: 'change',
};

/**
 * @constant
 * @type {string}
 */
export const Icon = `${IMG}/geolocation.png`;

/**
 * @classdesc
 * <b>Javascript ES5</b>
 * ```js
 * const geolocation = new cercaliagl.Geolocation();
 * ```
 *
 * Utility the get current geolocation. User browser must be allow to get GPS position.
 * It is possible to handle an event when position is changed.
 *
 * Example usage.
 *
 * ```
 * const map = new Map({
 *   target: 'map',
 *   controls: [],
 * });
 *
 * map.whenReady(() => {
 *   const geolocation = new cercaliagl.Geolocation();
 *   geolocation.setTracking(true);
 *   geolocation.on('change', () => {
  *     const marker = new cercaliagl.Marker({
 *       position: geolocation.getPosition(),
 *       icon: new cercaliagl.Icon({ src: 'resources/img/geolocation.png' }),
 *     });
 *     const geometry = geolocation.getAccuracyGeometry();
 *     const feature = new cercaliagl.Feature({ geometry: geometry });
 *     map.addMarker(marker);
 *     map.addFeature(feature);
 *     map.centerToFeatures([feature]);
 *   });
 * });
 * ```
 *
 * @extends {EventEmitter}
 * @api
 */
class Geolocation extends EventEmitter {
  constructor() {
    super();

    /**
     * @private
     * @type {LonLat}
     */
    this.position_ = null;

    /**
     * @private
     * @type {boolean}
     */
    this.tracking_ = false;

    this.on(EventType.TRACKING, this.handleTrackingChanged_.bind(this));
  }

  /**
   * @private
   */
  handleTrackingChanged_() {
    if ('geolocation' in navigator) {
      const tracking = this.getTracking();
      if (tracking && this.watchId_ === undefined) {
        this.watchId_ = navigator.geolocation.watchPosition(
          this.positionChange_.bind(this),
          (error) => console.error('Error getting geolocation position', error), // eslint-disable-line no-console
          {},
        );
      } else if (!tracking && this.watchId_ !== undefined) {
        navigator.geolocation.clearWatch(this.watchId_);
        this.watchId_ = undefined;
      }
    }
  }

  /**
   * @return {boolean} Tracking
   * @api
   */
  getTracking() {
    return this.tracking_;
  }

  /**
   * @param {boolean} tracking Tracking
   * @api
   */
  setTracking(tracking) {
    this.tracking_ = tracking;
    this.emit(EventType.TRACKING, this.tracking_);
  }

  /**
   * @return {LonLat}
   * @api
   */
  getPosition() {
    return this.position_;
  }

  /**
   * @return {number}
   * @api
   */
  getAccuracy() {
    return this.accuracy_;
  }

  /**
   * @return {number}
   * @api
   */
  getAltitudeAccuracy() {
    return this.altitudeAccuracy_;
  }

  /**
   * @return {number}
   * @api
   */
  getSpeed() {
    return this.speed_;
  }

  /**
   * @return {number}
   * @api
   */
  getHeading() {
    return this.heading_;
  }

  /**
   * @private
   * @param {Position} position position event.
   */
  positionChange_(position) {
    const {
      accuracy, altitude, altitudeAccuracy, heading, latitude, longitude, speed,
    } = position.coords;
    this.accuracy_ = accuracy;
    this.altitude_ = altitude;
    this.altitudeAccuracy_ = altitudeAccuracy;
    this.heading_ = heading;
    this.position_ = new LonLat(longitude, latitude);
    this.speed_ = speed;
    this.emit(EventType.CHANGE);
  }

  /**
   * @api
   * @return {import("./util").GeometryGeoJSON} geometry
   */
  getAccuracyGeometry() {
    return createCircleGeometry(this.position_, this.accuracy_);
  }
}

export default Geolocation;
